import * as XLSX from 'xlsx';
import { supabase } from './supabaseClient';

// Accounts allowed to use the job application tracker. Keep in sync with
// public.can_use_job_tracker() in supabase_schema.sql, which enforces it server-side.
export const JOB_TRACKER_EMAILS = [
  'fatlindab2019@gmail.com',
  'granitkurrumeli@gmail.com',
];

export const canUseJobTracker = (user) => (
  JOB_TRACKER_EMAILS.includes(String(user?.email || '').toLowerCase())
);

export function getUserEmailShortcuts(user, applications = []) {
  const set = new Set();
  const currentEmail = String(user?.email || '').trim().toLowerCase();
  if (currentEmail) {
    set.add(currentEmail);
  }
  if (Array.isArray(applications)) {
    applications.forEach((app) => {
      const email = String(app?.response_email_account || '').trim().toLowerCase();
      if (email && email.includes('@')) {
        set.add(email);
      }
    });
  }
  return Array.from(set);
}

export const JOB_STATUSES = [
  {
    id: 'saved',
    label: 'Për të aplikuar',
    labelSq: 'Për të aplikuar',
    labelDe: 'Merkliste / Zu bewerben',
    dot: 'bg-zinc-400 dark:bg-zinc-500',
    pill: 'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700',
    column: 'border-zinc-200/80 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-900/40',
  },
  {
    id: 'applied',
    label: 'Aplikuar',
    labelSq: 'Aplikuar',
    labelDe: 'Beworben',
    dot: 'bg-sky-500 dark:bg-sky-400',
    pill: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/50 dark:text-sky-300 dark:border-sky-800',
    column: 'border-sky-200/70 dark:border-sky-900/40 bg-sky-50/30 dark:bg-sky-950/20',
  },
  {
    id: 'no_response',
    label: "S'kam marrë përgjigje",
    labelSq: "S'kam marrë përgjigje",
    labelDe: 'Keine Rückmeldung',
    dot: 'bg-amber-500 dark:bg-amber-400',
    pill: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800',
    column: 'border-amber-200/70 dark:border-amber-900/40 bg-amber-50/30 dark:bg-amber-950/20',
  },
  {
    id: 'test',
    label: 'Test / detyrë',
    labelSq: 'Test / detyrë',
    labelDe: 'Test / Aufgabe',
    dot: 'bg-violet-500 dark:bg-violet-400',
    pill: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/50 dark:text-violet-300 dark:border-violet-800',
    column: 'border-violet-200/70 dark:border-violet-900/40 bg-violet-50/30 dark:bg-violet-950/20',
  },
  {
    id: 'interview',
    label: 'Intervistë',
    labelSq: 'Intervistë',
    labelDe: 'Vorstellungsgespräch',
    dot: 'bg-amber-400 dark:bg-amber-300',
    pill: 'bg-amber-50 text-amber-900 border-amber-300 dark:bg-amber-950/60 dark:text-amber-200 dark:border-amber-700',
    column: 'border-amber-300/70 dark:border-amber-800/40 bg-amber-50/40 dark:bg-amber-950/25',
  },
  {
    id: 'follow_up',
    label: 'Follow-up dërguar',
    labelSq: 'Follow-up dërguar',
    labelDe: 'Nachgehakt (Follow-up)',
    dot: 'bg-indigo-500 dark:bg-indigo-400',
    pill: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-300 dark:border-indigo-800',
    column: 'border-indigo-200/70 dark:border-indigo-900/40 bg-indigo-50/30 dark:bg-indigo-950/20',
  },
  {
    id: 'offer',
    label: 'Ofertë',
    labelSq: 'Ofertë',
    labelDe: 'Angebot erhalten',
    dot: 'bg-emerald-500 dark:bg-emerald-400',
    pill: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800',
    column: 'border-emerald-200/70 dark:border-emerald-900/40 bg-emerald-50/30 dark:bg-emerald-950/20',
  },
  {
    id: 'rejected',
    label: 'Refuzuar',
    labelSq: 'Refuzuar',
    labelDe: 'Abgelehnt',
    dot: 'bg-rose-500 dark:bg-rose-400',
    pill: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800',
    column: 'border-rose-200/70 dark:border-rose-900/40 bg-rose-50/30 dark:bg-rose-950/20',
  },
  {
    id: 'declined',
    label: 'E refuzova unë',
    labelSq: 'E refuzova unë',
    labelDe: 'Selbst abgelehnt',
    dot: 'bg-zinc-600 dark:bg-zinc-400',
    pill: 'bg-zinc-100 text-zinc-800 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700',
    column: 'border-zinc-300/70 dark:border-zinc-700/60 bg-zinc-100/40 dark:bg-zinc-900/40',
  },
];

export const JOB_PLATFORMS = [
  'StepStone.de',
  'LinkedIn',
  'XING',
  'Indeed.de',
  'Bundesagentur für Arbeit',
  'join.com',
  'KosovaJob',
  'SuperPuna',
  'Faqja e kompanisë',
  'Email',
  'Tjetër',
];

export function getStatusMeta(statusId, lang = 'sq') {
  const found = JOB_STATUSES.find((s) => s.id === statusId) || JOB_STATUSES[1];
  return {
    ...found,
    displayName: lang === 'de' ? found.labelDe : found.labelSq,
  };
}

export function getPlatformLabel(platform, lang = 'sq') {
  if (!platform) return '';
  const id = typeof platform === 'object' ? (platform.id || platform.label || '') : String(platform);
  if (lang === 'de') {
    if (id === 'Faqja e kompanisë') return 'Unternehmens-Website';
    if (id === 'Email') return 'E-Mail';
    if (id === 'Tjetër') return 'Sonstiges';
  }
  return id;
}

const LOCAL_STORAGE_KEY = 'job_applications_cache_v2';

function getLocalApplications() {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.error('Error reading local job applications:', err);
    return [];
  }
}

function saveLocalApplications(apps) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(apps));
  } catch (err) {
    console.error('Error caching job applications:', err);
  }
}

export async function fetchJobApplications() {
  try {
    const { data, error } = await supabase
      .from('job_applications')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    if (Array.isArray(data)) {
      saveLocalApplications(data);
      return data;
    }
    return [];
  } catch (err) {
    console.warn('Supabase fetch failed, checking local cache:', err.message);
    const cached = getLocalApplications();
    if (cached.length > 0) {
      return cached;
    }
    throw err;
  }
}

export async function createJobApplication(applicationData) {
  const localId = 'loc_' + Date.now();
  const optimisticItem = {
    id: localId,
    ...applicationData,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  try {
    // Attempt insert to Supabase
    const { data, error } = await supabase
      .from('job_applications')
      .insert([applicationData])
      .select()
      .single();

    if (error) {
      // If error is missing column (e.g. response_email_account)
      if (error.message && error.message.includes('response_email_account')) {
        console.warn('response_email_account column missing in Supabase. Retrying without it...');
        const { response_email_account, ...rest } = applicationData;
        const retry = await supabase
          .from('job_applications')
          .insert([rest])
          .select()
          .single();
        if (retry.error) throw retry.error;
        const result = { ...retry.data, response_email_account };
        const current = getLocalApplications();
        saveLocalApplications([result, ...current]);
        return result;
      }
      throw error;
    }

    const current = getLocalApplications();
    saveLocalApplications([data, ...current.filter((c) => c.id !== data.id)]);
    return data;
  } catch (err) {
    console.warn('Supabase create failed, saving to local cache:', err.message);
    const current = getLocalApplications();
    saveLocalApplications([optimisticItem, ...current]);
    // Still return the created item so user is never blocked
    return optimisticItem;
  }
}

export async function updateJobApplication(id, updates) {
  try {
    const { data, error } = await supabase
      .from('job_applications')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      if (error.message && error.message.includes('response_email_account')) {
        const { response_email_account, ...rest } = updates;
        const retry = await supabase
          .from('job_applications')
          .update({ ...rest, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single();
        if (retry.error) throw retry.error;
        const result = { ...retry.data, response_email_account };
        const current = getLocalApplications();
        saveLocalApplications(current.map((item) => (item.id === id ? { ...item, ...result } : item)));
        return result;
      }
      throw error;
    }

    const current = getLocalApplications();
    saveLocalApplications(current.map((item) => (item.id === id ? data : item)));
    return data;
  } catch (err) {
    console.warn('Supabase update failed, updating local cache:', err.message);
    const current = getLocalApplications();
    const updated = current.map((item) => (item.id === id ? { ...item, ...updates, updated_at: new Date().toISOString() } : item));
    saveLocalApplications(updated);
    return updated.find((item) => item.id === id) || { id, ...updates };
  }
}

export async function deleteJobApplication(id) {
  try {
    const { error } = await supabase
      .from('job_applications')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (err) {
    console.warn('Supabase delete failed, removing from local cache:', err.message);
  } finally {
    const current = getLocalApplications();
    saveLocalApplications(current.filter((item) => item.id !== id));
  }
  return true;
}

export async function updateJobStatus(id, newStatus) {
  return updateJobApplication(id, { status: newStatus });
}

/**
 * Generates and downloads a clean Excel (.xlsx) spreadsheet of job applications
 */
export function exportApplicationsToExcel(applications, language = 'sq') {
  const isDe = language === 'de';

  const rows = applications.map((app, index) => {
    const statusMeta = getStatusMeta(app.status, language);
    const platformLabel = getPlatformLabel(app.platform, language);

    return {
      'Nr.': index + 1,
      [isDe ? 'Unternehmen' : 'Kompania']: app.company || '',
      [isDe ? 'Position / Rolle' : 'Pozita / Roli']: app.role || '',
      [isDe ? 'Status' : 'Statusi']: statusMeta.displayName,
      [isDe ? 'Plattform' : 'Platforma']: platformLabel,
      [isDe ? 'Bewerbungsdatum' : 'Data e aplikimit']: app.applied_on || '',
      [isDe ? 'Nächster Termin / Schritt' : 'Hapi i radhës / Intervistë']: app.next_step_on || '',
      [isDe ? 'E-Mail-Konto (Empfangene Antwort)' : 'Llogaria e emailit (ku u mor përgjigja)']:
        app.response_email_account || '',
      [isDe ? 'Kontakt- / Absender-E-Mail' : 'Email kontakti / dërguesi']: app.contact_email || '',
      [isDe ? 'E-Mail-Betreff (Subject)' : 'Titulli i email-it (Subject)']: app.email_subject || '',
      [isDe ? 'Gehalt / Angebot' : 'Paga / Ofertë']: app.salary || '',
      [isDe ? 'Stellenanzeigen-Link' : 'Vegëza e shpalljes']: app.link || '',
      [isDe ? 'Notizen' : 'Shënime']: app.notes || '',
      [isDe ? 'Erfasst am' : 'Regjistruar më']: app.created_at ? app.created_at.split('T')[0] : '',
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);

  // Set explicit column widths for readability in Excel
  worksheet['!cols'] = [
    { wch: 6 },  // Nr.
    { wch: 26 }, // Kompania
    { wch: 30 }, // Pozita
    { wch: 22 }, // Statusi
    { wch: 18 }, // Platforma
    { wch: 16 }, // Data e aplikimit
    { wch: 20 }, // Hapi i radhës
    { wch: 30 }, // Llogaria ku u mor përgjigja
    { wch: 28 }, // Email kontakti
    { wch: 42 }, // Titulli i emailit
    { wch: 16 }, // Paga
    { wch: 35 }, // Vegëza
    { wch: 45 }, // Shënime
    { wch: 15 }, // Regjistruar më
  ];

  const workbook = XLSX.utils.book_new();
  const sheetName = isDe ? 'Bewerbungen' : 'Aplikimet';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const dateStr = new Date().toISOString().split('T')[0];
  const filename = isDe
    ? `Bewerbungen_${dateStr}.xlsx`
    : `Aplikimet_Pune_${dateStr}.xlsx`;

  XLSX.writeFile(workbook, filename);
}
